import getMode from "../tools/getMode";
import mergeDeep from "../tools/merge";
import downloadURI from "../tools/downloadURI";
import QRSVG from "./QRSVG";
import drawTypes from "../constants/drawTypes";

import defaultOptions, { RequiredOptions } from "./QROptions";
import sanitizeOptions from "../tools/sanitizeOptions";
import { FileExtension, QRCode, Options, DownloadOptions, ExtensionFunction } from "../types";
import qrcode from "qrcode-generator";
import { getScaledFrameConfig, isFrameTypeAvailable } from "../frames";

export default class QRCodeStyling {
  _options: RequiredOptions;
  _container?: HTMLElement;
  _canvas?: HTMLCanvasElement;
  _svg?: SVGElement;
  _qr?: QRCode;
  _extension?: ExtensionFunction;
  _canvasDrawingPromise?: Promise<void>;
  _svgDrawingPromise?: Promise<void>;

  // Static matrix cache for performance optimization
  private static _matrixCache = new Map<string, QRCode>();
  private static _cacheOrder = new Set<string>();
  private static _maxCacheSize = 100;
  private static _cacheHits = 0;
  private static _cacheMisses = 0;

  constructor(options?: Partial<Options>) {
    this._options = options ? sanitizeOptions(mergeDeep(defaultOptions, options) as RequiredOptions) : defaultOptions;
    this.update();
  }

  static _generateCacheKey(
    data: string,
    qrOptions: { typeNumber: any; errorCorrectionLevel: any; mode?: any }
  ): string {
    // Create hash from only the options that affect QR matrix generation
    const keyData = {
      data,
      typeNumber: qrOptions.typeNumber,
      errorCorrectionLevel: qrOptions.errorCorrectionLevel,
      mode: qrOptions.mode || getMode(data)
    };

    // Simple but effective hash function
    const str = JSON.stringify(keyData);
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  }

  static _evictOldestCacheEntries(): void {
    const toEvict = Math.max(1, Math.floor(this._maxCacheSize * 0.2)); // Evict 20%
    let evicted = 0;

    for (const key of this._cacheOrder) {
      this._matrixCache.delete(key);
      this._cacheOrder.delete(key);
      evicted++;
      if (evicted >= toEvict) break;
    }
  }

  static _getMatrixCached(data: string, qrOptions: { typeNumber: any; errorCorrectionLevel: any; mode?: any }): QRCode {
    const cacheKey = this._generateCacheKey(data, qrOptions);

    // Check cache first
    if (this._matrixCache.has(cacheKey)) {
      this._cacheHits++;
      // Move to end of LRU order
      this._cacheOrder.delete(cacheKey);
      this._cacheOrder.add(cacheKey);
      return this._matrixCache.get(cacheKey)!;
    }

    // Cache miss - generate new matrix
    this._cacheMisses++;

    const qr = qrcode(qrOptions.typeNumber as any, qrOptions.errorCorrectionLevel as any);
    qr.addData(data, (qrOptions.mode || getMode(data)) as any);
    qr.make();

    // Add to cache with LRU management
    if (this._matrixCache.size >= this._maxCacheSize) {
      this._evictOldestCacheEntries();
    }

    this._matrixCache.set(cacheKey, qr);
    this._cacheOrder.add(cacheKey);

    return qr;
  }

  static clearMatrixCache(): void {
    this._matrixCache.clear();
    this._cacheOrder.clear();
    this._cacheHits = 0;
    this._cacheMisses = 0;
  }

  static getMatrixCacheStats(): { size: number; hits: number; misses: number; hitRate: number } {
    const total = this._cacheHits + this._cacheMisses;
    return {
      size: this._matrixCache.size,
      hits: this._cacheHits,
      misses: this._cacheMisses,
      hitRate: total > 0 ? (this._cacheHits / total) * 100 : 0
    };
  }

  static setMatrixCacheSize(size: number): void {
    this._maxCacheSize = Math.max(1, size);
    if (this._matrixCache.size > this._maxCacheSize) {
      this._evictOldestCacheEntries();
    }
  }

  static _clearContainer(container?: HTMLElement): void {
    if (container) {
      container.innerHTML = "";
    }
  }

  /**
   * Get the actual output dimensions accounting for frames
   */
  _getOutputDimensions(): { width: number; height: number } {
    // Check if frame is present and valid
    const frameOptions = this._options.frameOptions;
    if (frameOptions.type && frameOptions.type !== "none" && isFrameTypeAvailable(frameOptions.type)) {
      // Calculate frame dimensions
      const scaledFrame = getScaledFrameConfig(frameOptions.type, this._options.width);
      return {
        width: scaledFrame.width,
        height: scaledFrame.height
      };
    }

    // No frame - use original dimensions
    return {
      width: this._options.width,
      height: this._options.height
    };
  }

  _setupSvg(): void {
    if (!this._qr) {
      return;
    }
    const qrSVG = new QRSVG(this._options);

    this._svg = qrSVG.getElement();
    this._svgDrawingPromise = qrSVG.drawQR(this._qr).then(() => {
      if (!this._svg) return;
      this._extension?.(qrSVG.getElement(), this._options);
    });
  }

  _setupCanvas(): void {
    if (!this._qr) {
      return;
    }

    // Get correct dimensions (accounting for frames)
    const dimensions = this._getOutputDimensions();

    // Render at 2x resolution for better quality
    const scale = 2;
    this._canvas = document.createElement("canvas");
    this._canvas.width = dimensions.width * scale;
    this._canvas.height = dimensions.height * scale;

    // Scale back down for display
    this._canvas.style.width = dimensions.width + "px";
    this._canvas.style.height = dimensions.height + "px";

    this._setupSvg();
    this._canvasDrawingPromise = this._svgDrawingPromise?.then(() => {
      if (!this._svg) return;

      const svg = this._svg;
      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = btoa(xml);
      const image64 = "data:image/svg+xml;base64," + svg64;
      const image = new Image();

      return new Promise((resolve, reject) => {
        image.onload = (): void => {
          const ctx = this._canvas?.getContext("2d");
          if (ctx && this._canvas) {
            // Scale the context to match our high-DPI canvas
            const scale = 2;
            ctx.scale(scale, scale);

            // Disable image smoothing for crisp QR codes
            ctx.imageSmoothingEnabled = false;
            // Legacy browser support
            (ctx as any).webkitImageSmoothingEnabled = false;
            (ctx as any).mozImageSmoothingEnabled = false;
            (ctx as any).msImageSmoothingEnabled = false;

            ctx.drawImage(image, 0, 0);
          }
          // Clear event listeners to prevent memory leaks
          image.onload = null;
          image.onerror = null;
          resolve();
        };

        image.onerror = (): void => {
          // Clear event listeners to prevent memory leaks
          image.onload = null;
          image.onerror = null;
          reject(new Error("Failed to load SVG image for canvas conversion"));
        };

        image.src = image64;
      });
    });
  }

  async _getElement(extension: FileExtension = "png"): Promise<SVGElement | HTMLCanvasElement | undefined> {
    if (!this._qr) throw "QR code is empty";

    if (extension.toLowerCase() === "svg") {
      if (!this._svg || !this._svgDrawingPromise) {
        this._setupSvg();
      }
      await this._svgDrawingPromise;
      return this._svg;
    } else {
      if (!this._canvas || !this._canvasDrawingPromise) {
        this._setupCanvas();
      }
      await this._canvasDrawingPromise;
      return this._canvas;
    }
  }

  _cleanup(): void {
    // Cancel any pending promises by setting them to resolved state
    this._canvasDrawingPromise = Promise.resolve();
    this._svgDrawingPromise = Promise.resolve();

    // Clear DOM element references to allow garbage collection
    this._svg = undefined;
    this._canvas = undefined;
    this._qr = undefined;

    // Note: We don't clear _container as it may be reused
    // Note: We don't clear _options as they may be reused
    // Note: We don't clear _extension as it should persist
  }

  update(options?: Partial<Options>): void {
    // Clean up existing resources before creating new ones
    this._cleanup();
    QRCodeStyling._clearContainer(this._container);

    this._options = options ? sanitizeOptions(mergeDeep(this._options, options) as RequiredOptions) : this._options;

    if (!this._options.data) {
      return;
    }

    // Use cached matrix for performance optimization
    this._qr = QRCodeStyling._getMatrixCached(this._options.data, this._options.qrOptions);

    if (this._options.type === drawTypes.canvas) {
      this._setupCanvas();
    } else {
      this._setupSvg();
    }

    // If there was a previous container, append to it asynchronously
    // This maintains backward compatibility while fixing the race condition
    if (this._container) {
      // Use setTimeout to make this async and non-blocking
      setTimeout(() => {
        this.append(this._container).catch((error) => {
          console.warn("QRCodeStyling: Failed to auto-append after update:", error);
        });
      }, 0);
    }
  }

  append(container?: HTMLElement): Promise<void> {
    if (!container) {
      return Promise.resolve();
    }

    if (typeof container.appendChild !== "function") {
      throw "Container should be a single DOM node";
    }

    // Wait for the appropriate rendering promise to complete before appending
    const renderingPromise =
      this._options.type === drawTypes.canvas ? this._canvasDrawingPromise : this._svgDrawingPromise;

    if (!renderingPromise) {
      // No rendering in progress, append immediately if element exists
      this._appendElementToContainer(container);
      this._container = container;
      return Promise.resolve();
    }

    // Wait for rendering to complete, then append
    return renderingPromise
      .then(() => {
        this._appendElementToContainer(container);
        this._container = container;
      })
      .catch((error) => {
        // Still set container even if rendering failed, for cleanup purposes
        this._container = container;
        throw error;
      });
  }

  private _appendElementToContainer(container: HTMLElement): void {
    if (this._options.type === drawTypes.canvas) {
      if (this._canvas) {
        container.appendChild(this._canvas);
      }
    } else {
      if (this._svg) {
        container.appendChild(this._svg);
      }
    }
  }

  applyExtension(extension: ExtensionFunction): void {
    if (!extension) {
      throw "Extension function should be defined.";
    }

    this._extension = extension;
    this.update();
  }

  deleteExtension(): void {
    this._extension = undefined;
    this.update();
  }

  destroy(): void {
    // Clean up all internal references and clear container
    this._cleanup();
    QRCodeStyling._clearContainer(this._container);
    this._container = undefined;
    this._extension = undefined;

    // Clear options to break any circular references
    this._options = {} as RequiredOptions;
  }

  async getRawData(extension: FileExtension = "png"): Promise<Blob | null> {
    if (!this._qr) throw "QR code is empty";
    const element = await this._getElement(extension);

    if (!element) {
      return null;
    }

    if (extension.toLowerCase() === "svg") {
      const serializer = new XMLSerializer();
      const source = serializer.serializeToString(element);

      return new Blob(['<?xml version="1.0" standalone="no"?>\r\n' + source], { type: "image/svg+xml" });
    } else {
      return new Promise((resolve) => (element as HTMLCanvasElement).toBlob(resolve, `image/${extension}`, 1));
    }
  }

  async download(downloadOptions?: Partial<DownloadOptions> | string): Promise<void> {
    if (!this._qr) throw "QR code is empty";
    let extension = "png" as FileExtension;
    let name = "qr";

    //TODO remove deprecated code in the v2
    if (typeof downloadOptions === "string") {
      extension = downloadOptions as FileExtension;
      console.warn(
        "Extension is deprecated as argument for 'download' method, please pass object { name: '...', extension: '...' } as argument"
      );
    } else if (typeof downloadOptions === "object" && downloadOptions !== null) {
      if (downloadOptions.name) {
        name = downloadOptions.name;
      }
      if (downloadOptions.extension) {
        extension = downloadOptions.extension;
      }
    }

    const element = await this._getElement(extension);

    if (!element) {
      return;
    }

    if (extension.toLowerCase() === "svg") {
      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(element);

      source = '<?xml version="1.0" standalone="no"?>\r\n' + source;
      const url = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(source);
      downloadURI(url, `${name}.svg`);
    } else {
      const url = (element as HTMLCanvasElement).toDataURL(`image/${extension}`);
      downloadURI(url, `${name}.${extension}`);
    }
  }
}
